import type { SupabaseClient } from '@supabase/supabase-js'
import { getActivePinia } from 'pinia'
import { useUserStore } from '~/stores/user'
import { useBalanceStore } from '~/stores/balance'
import { useCharacterStore } from '~/stores/character'
import { useOreStore } from '~/stores/ore'
import { useFreeBetsStore } from '~/stores/freeBets'
import { useUIStore } from '#layers/base/stores/ui'
import { clearTopbarCache } from './topbarCache'

/**
 * Emergency synchronous client-side cleanup for 401 Unauthorized responses.
 *
 * Runs without depending on Vue/Nuxt component context — uses `getActivePinia()`
 * + explicit pinia instance passing so stores can be accessed from anywhere
 * (e.g. inside a low-level $fetch interceptor).
 *
 * Contract:
 *   - Synchronous (no awaits, no promises returned)
 *   - Idempotent (safe to call multiple times)
 *   - Never throws (every step wrapped, swallowed errors logged via console.warn)
 *   - SSR-safe (no-ops on the server, all browser APIs gated by `typeof window`)
 *
 * Cleanup performed:
 *   1. localStorage.removeItem('aurumrun_session_id')
 *   2. clearTopbarCache()
 *   3. synchronous sweep of the `sb-<project-ref>-auth-token*` localStorage
 *      keys — see Step 1b for why this can't be left to signOut
 *   4. $supabase.auth.signOut({ scope: 'local' }) — fire-and-forget; clears
 *      in-memory auth state and fires SIGNED_OUT for realtime observers
 *   5. userStore.clearUser()
 *   6. balanceStore.clearBalance()
 *   7. characterStore.reset()
 *   8. oreStore.clearOre()
 *   9. freeBetsStore.clearFreeBets()
 *  10. uiStore.openLoginModal()
 *
 * Note: this is the LAST-RESORT path. The normal logout flow lives in
 * `useAuth.ts#logout()` and should be preferred when component context exists.
 */
export function handle401Logout(): void {
  // SSR guard — there is no client state to clean on the server.
  if (typeof window === 'undefined') return

  // Step 1: drop the session id from localStorage. Cheapest, most important.
  try {
    localStorage.removeItem('aurumrun_session_id')
  } catch (err) {
    console.warn('[handle401Logout] localStorage.removeItem failed:', err)
  }

  // Drop the optimistic topbar identity cache so the next reload doesn't paint
  // a logged-in shell for a session we just tore down. Above the pinia guard so
  // it runs even in the early-bootstrap 401 case (spec C5).
  try {
    clearTopbarCache()
  } catch (err) {
    console.warn('[handle401Logout] clearTopbarCache failed:', err)
  }

  // Step 1b: synchronously sweep Supabase auth tokens (`sb-<ref>-auth-token`
  // and its `-code-verifier` sibling; project-ref is env-dependent, so match
  // by shape). Left to signOut alone this would be network-bound — auth-js
  // calls admin.signOut BEFORE removing the local session — so the hard
  // redirect in $fetch.ts STEP 3 could tear the page down first while the
  // token is still in storage. A surviving token makes
  // auth.client.ts re-send /auth-session on the next boot → another 401 →
  // another reload, forever (prod incident: infinite reload loop on iOS
  // Chrome). Sweeping synchronously means the next boot starts logged-out
  // even if signOut never completes.
  //
  // Not airtight on its own: an auto-refresh already in flight resolves into
  // `_saveSession` and rewrites the key we just deleted. The pathname guard
  // in $fetch.ts STEP 3 is what contains that — the page survives, so the
  // async signOut below still runs (it takes the auth lock only after the
  // refresh releases it) and cleans the resurrected session up. Worst case
  // the refresh outlives the 10s lock timeout and signOut gives up: benign
  // and self-converging, since the next boot sweeps again and the pathname
  // guard keeps it from reloading — it costs one extra login modal.
  // The two layers must ship together; neither is sufficient alone.
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        localStorage.removeItem(key)
      }
    }
  } catch (err) {
    console.warn('[handle401Logout] supabase token sweep failed:', err)
  }

  // Step 1c: clear Supabase in-memory auth state. The sweep above already
  // removed the storage key, so signOut({ scope: 'local' }) skips the network
  // call and just resets in-memory state + fires SIGNED_OUT for realtime/auth
  // observers. Fire-and-forget to keep the function synchronous; any failure
  // is non-fatal — the navigation kicked off by the login modal opener below
  // force-resets the visible state.
  try {
    const nuxtApp = tryUseNuxtApp()
    const supabase = nuxtApp?.$supabase as SupabaseClient | undefined
    if (supabase) {
      supabase.auth.signOut({ scope: 'local' }).catch((err: unknown) => {
        console.warn('[handle401Logout] supabase signOut failed:', err)
      })
    }
  } catch (err) {
    console.warn('[handle401Logout] supabase signOut dispatch failed:', err)
  }

  // Pinia may not be installed yet (e.g. very early bootstrap). Bail out
  // gracefully — the localStorage clear above is the critical part.
  const pinia = getActivePinia()
  if (!pinia) {
    console.warn('[handle401Logout] no active pinia instance — skipping store cleanup')
    return
  }

  // Each store call is wrapped individually so one failure does not abort
  // the rest of the cleanup chain.
  try {
    useUserStore(pinia).clearUser()
  } catch (err) {
    console.warn('[handle401Logout] userStore.clearUser failed:', err)
  }

  try {
    useBalanceStore(pinia).clearBalance()
  } catch (err) {
    console.warn('[handle401Logout] balanceStore.clearBalance failed:', err)
  }

  try {
    useCharacterStore(pinia).reset()
  } catch (err) {
    console.warn('[handle401Logout] characterStore.reset failed:', err)
  }

  try {
    useOreStore(pinia).clearOre()
  } catch (err) {
    console.warn('[handle401Logout] oreStore.clearOre failed:', err)
  }

  try {
    useFreeBetsStore(pinia).clearFreeBets()
  } catch (err) {
    console.warn('[handle401Logout] freeBetsStore.clearFreeBets failed:', err)
  }

  try {
    useUIStore(pinia).openLoginModal()
  } catch (err) {
    console.warn('[handle401Logout] uiStore.openLoginModal failed:', err)
  }
}
